/**
 * src/components/approvals/ApprovalCard.tsx
 * Glassmorphic card rendering an approval request item with status badges and Admin action buttons
 */

import React, { useState } from 'react';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ArrowUpRight, 
  ArrowDownLeft, 
  User, 
  Check, 
  X,
  MessageSquare,
  ShieldCheck,
  Paperclip
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassBadge } from '../ui/GlassBadge';
import { LightboxModal } from '../ui/LightboxModal';
import type { ApprovalItem } from '../../services/api';

interface ApprovalCardProps {
  item: ApprovalItem;
  isAdmin: boolean;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  isProcessing?: boolean;
}

export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  item,
  isAdmin,
  onApprove,
  onReject,
  isProcessing = false,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const isPaid = item.transaction_type === 'paid';
  const isPending = item.status === 'pending';
  const isApproved = item.status === 'approved';
  const isRejected = item.status === 'rejected';

  // Format date nicely
  const formatDate = (dateString: string) => {
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div
      className="ios-inset-group"
      style={{
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        position: 'relative'
      }}
    >
      {/* Top Header: Submitter info and Status Pill */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Submitter Avatar Squircle */}
          <div
            className="ios-icon-squircle blue"
            style={{
              width: '38px',
              height: '38px',
              fontSize: '0.9375rem',
              fontWeight: 700
            }}
          >
            {item.submitter_name ? item.submitter_name.charAt(0).toUpperCase() : <User size={16} />}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9375rem', letterSpacing: '-0.015em' }}>
                {item.submitter_name}
              </span>
              <span style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 'var(--radius-pill)',
                background: isPaid ? 'rgba(255, 69, 58, 0.15)' : 'rgba(48, 209, 88, 0.15)',
                color: isPaid ? 'var(--ios-red)' : 'var(--ios-green)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                {isPaid ? <ArrowDownLeft size={10} /> : <ArrowUpRight size={10} />}
                {isAdmin
                  ? (isPaid ? 'PAID BY ADMIN (DEBIT -)' : 'RECEIVED BY ADMIN (CREDIT +)')
                  : (isPaid ? 'RECEIVED BY ME (DEBIT -)' : 'PAID BY ME (CREDIT +)')}
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px', fontWeight: 500 }}>
              Submitted {formatDate(item.created_at || item.transaction_date)}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div>
          {isPending && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(255, 159, 10, 0.15)',
              border: '0.5px solid rgba(255, 159, 10, 0.35)',
              color: 'var(--ios-orange)',
              fontSize: '0.72rem',
              fontWeight: 700
            }}>
              <Clock size={12} />
              Under Review
            </div>
          )}

          {isApproved && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(48, 209, 88, 0.15)',
              border: '0.5px solid rgba(48, 209, 88, 0.35)',
              color: 'var(--ios-green)',
              fontSize: '0.72rem',
              fontWeight: 700
            }}>
              <CheckCircle2 size={12} />
              Approved
            </div>
          )}

          {isRejected && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(255, 69, 58, 0.15)',
              border: '0.5px solid rgba(255, 69, 58, 0.35)',
              color: 'var(--ios-red)',
              fontSize: '0.72rem',
              fontWeight: 700
            }}>
              <XCircle size={12} />
              Rejected
            </div>
          )}
        </div>
      </div>

      {/* Main Body: Amount & Description */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 14px',
        borderRadius: '14px',
        background: 'rgba(118, 118, 128, 0.12)',
        border: 'var(--border-hairline)'
      }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '2px' }}>
            Category: <strong style={{ color: 'var(--text-secondary)' }}>{item.category_name || 'General'}</strong>
          </div>
          <div style={{
            fontSize: '0.9375rem',
            color: 'var(--text-primary)',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {item.description}
          </div>
          {item.receipt_url && (
            <div style={{ marginTop: '4px' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLightboxOpen(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'rgba(10, 132, 255, 0.12)',
                  border: '0.5px solid rgba(10, 132, 255, 0.3)',
                  color: 'var(--ios-blue)',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="View attached receipt image"
              >
                <Paperclip size={10} />
                <span>View Attached Receipt</span>
              </button>
            </div>
          )}
        </div>

        <div
          className="num-font"
          style={{
            fontSize: '1.25rem',
            fontWeight: 800,
            color: isPaid ? 'var(--ios-red)' : 'var(--ios-green)',
            letterSpacing: '-0.02em',
            whiteSpace: 'nowrap'
          }}
        >
          {isPaid ? '-' : '+'} ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>

      {/* Rejection / Reviewer Feedback Banner */}
      {isRejected && item.review_note && (
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '10px 12px',
          borderRadius: '10px',
          background: 'rgba(255, 69, 58, 0.1)',
          border: '0.5px solid rgba(255, 69, 58, 0.25)',
          color: 'var(--ios-red)',
          fontSize: '0.8125rem'
        }}>
          <MessageSquare size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Admin Note:</strong> {item.review_note}
          </div>
        </div>
      )}

      {/* Approved details */}
      {isApproved && item.reviewer_name && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '0.75rem',
          color: 'var(--text-tertiary)'
        }}>
          <ShieldCheck size={14} style={{ color: 'var(--ios-green)' }} />
          Reviewed & approved by <strong>{item.reviewer_name}</strong> on {formatDate(item.reviewed_at || '')}
        </div>
      )}

      {/* Admin Quick Action Buttons */}
      {isAdmin && isPending && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10px',
          paddingTop: '6px',
          borderTop: 'var(--border-hairline)'
        }}>
          <button
            type="button"
            className="glass-button glass-button-crimson"
            onClick={() => onReject && onReject(item.id)}
            disabled={isProcessing}
            style={{ width: '100%', justifyContent: 'center', borderRadius: '12px', padding: '10px' }}
          >
            <X size={15} />
            Reject
          </button>

          <button
            type="button"
            className="glass-button glass-button-emerald"
            onClick={() => onApprove && onApprove(item.id)}
            disabled={isProcessing}
            style={{ width: '100%', justifyContent: 'center', borderRadius: '12px', padding: '10px' }}
          >
            <Check size={15} />
            Approve
          </button>
        </div>
      )}

      {/* Lightbox Receipt Viewer */}
      {item.receipt_url && (
        <LightboxModal
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          imageUrl={item.receipt_url}
          title={`Approval Request #${item.id} — ${item.description}`}
        />
      )}
    </div>
  );
};
